import React, { useCallback, useEffect, useMemo, useState } from "react";
import { X, Folder, FolderTree, Plus } from "lucide-react";
import { Button } from "./ui/button";
import { InlineLoadSkeleton } from "./common/InlineLoadSkeleton";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { projectService } from "../services/projectService";
import { suiteService } from "../services/suiteService";
import sectionService from "../services/sectionService";
import { testcaseService } from "../services/testcaseService";
import toast from "react-hot-toast";

const STRUCTURE_MULTI = "MULTI_SUITE";
const STRUCTURE_MULTIPLE = "MULTIPLE_TEST_SUITES";

function flattenSections(sectionList, level = 0) {
  if (!Array.isArray(sectionList)) return [];
  let result = [];
  sectionList.forEach((section) => {
    if (!section) return;
    const displayName = section.display_name || section.name;
    const indent = "  ".repeat(level);
    result.push({
      section_id: section.section_id,
      name: `${indent}${displayName}`,
      level,
    });
    if (section.children?.length) {
      result = result.concat(flattenSections(section.children, level + 1));
    }
  });
  return result;
}

const stripMd = (s) =>
  String(s || "")
    .replace(/\*\*/g, "")
    .replace(/^[*_`]+|[*_`]+$/g, "")
    .trim();

/** Strip list markers and numbering from a step line. */
function parseStepActionLine(trimmed) {
  return stripMd(
    trimmed
      .replace(/^\d+[.)]\s+/, "")
      .replace(/^[-*]\s+/, "")
      .trim(),
  );
}

/** If the model inlined "Precondition: …" / "Expected result: …" inside the Steps list, strip and route. */
function tryExtractPrefixedSectionLine(raw, block) {
  const s = String(raw || "").trim();
  const preM = s.match(/^preconditions?\s*:\s*(.+)$/i);
  if (preM) {
    block.preconditionLines.push(preM[1].trim());
    return true;
  }
  const expM = s.match(/^expected results?\s*:\s*(.+)$/i);
  if (expM) {
    block.expectedResultLines.push(expM[1].trim());
    return true;
  }
  return false;
}

/**
 * LLMs often number an observation (redirect, message shown) as the next "step" instead of expected result.
 * Fold those lines into the previous step when they read like outcomes, not imperatives.
 */
function isLikelyStepOutcomeLine(text) {
  const t = String(text || "").trim();
  if (!t) return false;
  if (
    /^(Click|Enter|Type|Open|Navigate|Select|Submit|Log\s+in|Sign\s+in|Press|Tap|Wait|Verify|Check|Ensur|Confirm|Go\s+to|Launch|Close|Clear|Fill|Input|Use|Choose|Pick|Scroll|Swipe|Upload|Download|Set|Toggle|Enable|Disable)\b/i.test(
      t,
    )
  ) {
    return false;
  }
  if (
    /^(The user (?:is|will be|should be|has been|remains)|The application|The system|A (?:message|dialog|toast|modal|error|success)|An error (?:message)?|User is|It (?:is|should)|Redirect(?:s|ed)?\b|Displayed|Shown\b|No error|Successfully\b)/i.test(
      t,
    )
  ) {
    return true;
  }
  if (
    /^(?:is|are|was|were)\s+(?:displayed|shown|visible|hidden|logged)/i.test(t)
  )
    return true;
  return false;
}

function foldImpliedOutcomesInActionList(actionStrings) {
  const rows = [];
  for (const raw of actionStrings) {
    const t = String(raw || "").trim();
    if (!t) continue;
    if (isLikelyStepOutcomeLine(t) && rows.length) {
      const prev = rows[rows.length - 1];
      prev.expected_result = prev.expected_result
        ? `${prev.expected_result}\n${t}`.trim()
        : t;
    } else {
      rows.push({ action: t, expected_result: "" });
    }
  }
  return rows;
}

/** Merge bullets from an "Expected Results" section onto rows (1:1 if counts match, else append block to last row). */
function mergeSectionExpectedOntoRows(rows, expectedLines) {
  const expected = (expectedLines || []).map((e) => stripMd(e)).filter(Boolean);
  if (!expected.length || !rows.length) return rows;
  const n = rows.length;
  const m = expected.length;
  const out = rows.map((r) => ({ ...r }));
  if (m === n) {
    for (let i = 0; i < n; i++) {
      const ex = expected[i];
      out[i].expected_result = out[i].expected_result
        ? `${out[i].expected_result}\n${ex}`.trim()
        : ex;
    }
  } else {
    const block = expected.join("\n");
    const last = n - 1;
    out[last].expected_result = out[last].expected_result
      ? `${out[last].expected_result}\n${block}`.trim()
      : block;
  }
  return out;
}

/**
 * Turn raw step lines into { action, expected_result }[], then apply Expected Results section bullets.
 */
function pairStepsWithExpected(stepActions, expectedLines) {
  const actions = (stepActions || [])
    .map((a) => String(a || "").trim())
    .filter(Boolean);
  if (!actions.length) return [];
  let rows = foldImpliedOutcomesInActionList(actions);
  rows = mergeSectionExpectedOntoRows(rows, expectedLines);
  return rows;
}

function normalizeProjectCaseTemplate(t) {
  const u = String(t == null ? "" : t)
    .toUpperCase()
    .trim();
  if (u === "SINGLE_STEP") return "SINGLE_STEP";
  if (u === "BDD") return "BDD";
  return "MULTIPLE_STEPS";
}

/** SINGLE_STEP projects accept only one step; merge parsed multi-step content into one row. */
function collapseStepsForSingleStepTemplate(rows) {
  const list = Array.isArray(rows) ? rows : [];
  if (list.length === 0) {
    return {
      steps: [{ step_number: 1, action: "", expected_result: "" }],
      expected_result: "",
    };
  }
  if (list.length === 1) {
    const s = list[0];
    const action = String(s.action || "").trim();
    const er = String(s.expected_result || "").trim();
    return {
      steps: [{ step_number: 1, action, expected_result: er }],
      expected_result: er,
    };
  }
  const actionParts = [];
  const expParts = [];
  for (let i = 0; i < list.length; i++) {
    const s = list[i];
    const a = String(s.action || "").trim();
    const e = String(s.expected_result || "").trim();
    if (a) actionParts.push(`${i + 1}. ${a}`);
    if (e) expParts.push(e);
  }
  const combinedAction = actionParts.join("\n");
  const combinedExpected = expParts.join("\n\n");
  return {
    steps: [
      {
        step_number: 1,
        action: combinedAction,
        expected_result: combinedExpected,
      },
    ],
    expected_result: combinedExpected,
  };
}

/**
 * Recover structured steps when the only data is a flat newline blob (old imports or models that prefix lines).
 */
function restructureLegacyPrefixedLines(title, stepLines) {
  const pre = [];
  const exp = [];
  const actions = [];
  for (const line of stepLines) {
    const t = String(line || "").trim();
    if (!t) continue;
    const pm = t.match(/^preconditions?\s*:\s*(.+)$/i);
    if (pm) {
      pre.push(pm[1].trim());
      continue;
    }
    const em = t.match(/^expected results?\s*:\s*(.+)$/i);
    if (em) {
      exp.push(em[1].trim());
      continue;
    }
    actions.push(t);
  }
  if (!pre.length && !exp.length) return null;
  let rows = pairStepsWithExpected(actions, exp);
  if (!rows.length) {
    rows = [{ action: title, expected_result: exp.join("\n") }];
  }
  return {
    preconditions: pre.length ? pre.join("\n") : undefined,
    stepsStructured: rows,
  };
}

/**
 * Markdown blocks from AI: ### Test Case: Name / #### Title: / Preconditions / Steps / Expected Results
 */
function parseMarkdownTestCaseBlocks(text) {
  if (!text || typeof text !== "string") return [];

  const lines = text.split(/\r?\n/);
  const results = [];
  let block = null;
  let currentSection = null;

  const flush = () => {
    if (!block) return;
    const title =
      (block.formalTitle || block.shortTitle || "").trim() ||
      "Untitled test case";
    const preconditionsText = (block.preconditionLines || [])
      .map((l) => stripMd(l))
      .filter(Boolean)
      .join("\n")
      .trim();

    let stepsStructured = pairStepsWithExpected(
      block.stepActionLines,
      block.expectedResultLines,
    );

    if (!stepsStructured.length) {
      const fallbackExpected = (block.expectedResultLines || [])
        .map((l) => stripMd(l))
        .filter(Boolean)
        .join("\n");
      stepsStructured = [{ action: title, expected_result: fallbackExpected }];
    }

    const stepsPreview =
      stepsStructured
        .map((r) => r.action)
        .join("\n")
        .trim() || title;

    results.push({
      title,
      preconditions: preconditionsText || undefined,
      stepsStructured,
      steps: stepsPreview,
    });
    block = null;
    currentSection = null;
  };

  for (const raw of lines) {
    const trimmed = raw.trim();
    if (!trimmed) continue;

    // ### Test Case: Successful Login  (##–######; optional bold around "Test Case")
    const tcHead = trimmed.match(
      /^#{2,6}\s*\*{0,2}\s*Test Case\s*\*{0,2}\s*:\s*(.+)$/i,
    );
    if (tcHead) {
      flush();
      block = {
        shortTitle: stripMd(tcHead[1]),
        formalTitle: null,
        preconditionLines: [],
        stepActionLines: [],
        expectedResultLines: [],
      };
      currentSection = null;
      continue;
    }

    if (!block) continue;

    // #### Title: Verify …
    const titleLine = trimmed.match(
      /^#{1,6}\s*\*{0,2}\s*Title\s*\*{0,2}\s*:\s*(.+)$/i,
    );
    if (titleLine) {
      block.formalTitle = stripMd(titleLine[1]);
      continue;
    }

    // #### Preconditions / Steps / Expected Results (use \bSteps\b — not "Step 1.")
    const sec = trimmed.match(
      /^#{1,6}\s*\*{0,2}\s*(Preconditions?|Expected Results?|Steps)\b\s*\*{0,2}\s*:?\s*$/i,
    );
    if (sec) {
      const name = sec[1].toLowerCase();
      if (name.startsWith("precondition")) currentSection = "pre";
      else if (name.startsWith("step")) currentSection = "steps";
      else currentSection = "expected";
      continue;
    }

    // Same section titles without hashes (models often omit ####)
    if (!/^#{1,6}\s/.test(trimmed)) {
      const plainSec = trimmed.match(
        /^(?:\*\*)?\s*(Preconditions?|Expected Results?|Steps)\b(?:\*\*)?\s*:?\s*$/i,
      );
      if (plainSec) {
        const name = plainSec[1].toLowerCase();
        if (name.startsWith("precondition")) currentSection = "pre";
        else if (name.startsWith("step")) currentSection = "steps";
        else currentSection = "expected";
        continue;
      }
    }

    // Skip other markdown headings inside a block (e.g. stray ####)
    if (/^#{1,6}\s/.test(trimmed)) continue;

    if (currentSection === "pre") {
      let content = trimmed.replace(/^[-*]\s+/, "").trim();
      content = content.replace(/^preconditions?\s*:\s*/i, "").trim();
      if (content) block.preconditionLines.push(content);
    } else if (currentSection === "expected") {
      let content = trimmed.replace(/^[-*]\s+/, "").trim();
      content = content.replace(/^expected results?\s*:\s*/i, "").trim();
      if (content) block.expectedResultLines.push(content);
    } else if (currentSection === "steps" || currentSection === null) {
      const action = parseStepActionLine(trimmed);
      if (action && tryExtractPrefixedSectionLine(action, block)) continue;
      if (action) block.stepActionLines.push(action);
    }
  }
  flush();
  return results;
}

/** Numbered list under a "Test cases" heading (legacy format). */
function parseListStyleTestCases(text) {
  if (!text || typeof text !== "string") return [];

  const lines = text.split(/\r?\n/);
  const out = [];

  const isStopHeading = (t) =>
    /^(?:\*{0,2})\s*(test data|preconditions|steps to reproduce|selectors?|notes?)\s*:?\s*(?:\*{0,2})$/i.test(
      t,
    );

  const isTestCaseHeading = (t) =>
    /test cases/i.test(t) &&
    !/test data|preconditions|steps to reproduce/i.test(t);

  const parseItem = (t) => {
    const m = t.match(/^\s*\d+[.)]\s+(.*)$/);
    if (!m) return null;
    const body = m[1].trim();
    const colonIdx = body.indexOf(":");
    const rawTitle = (colonIdx >= 0 ? body.slice(0, colonIdx) : body).trim();
    const rawDesc = (colonIdx >= 0 ? body.slice(colonIdx + 1) : "").trim();
    const title = stripMd(rawTitle) || "Untitled test case";
    const steps = stripMd(rawDesc);
    return { title, steps };
  };

  let inTestCases = false;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    const heading = stripMd(trimmed).replace(/:$/, "").trim();

    if (isStopHeading(heading)) {
      inTestCases = false;
      continue;
    }

    if (isTestCaseHeading(heading)) {
      inTestCases = true;
      continue;
    }

    if (!inTestCases) continue;

    if (
      /^(positive|negative|edge)\b/i.test(heading) &&
      !/^\d+[.)]\s+/.test(trimmed)
    )
      continue;

    const item = parseItem(trimmed);
    if (item) out.push(item);
  }

  return out;
}

/** Parse assistant message into items for "Add to project" (legacy: { title, steps }; markdown blocks may include preconditions + stepsStructured). */
function parseTestCasesFromMessage(text) {
  if (!text || typeof text !== "string") return [];

  // Prefer markdown blocks whenever "### Test Case:" appears (any line); avoid a brittle ^-only gate.
  const md = parseMarkdownTestCaseBlocks(text);
  if (md.length) return md;

  return parseListStyleTestCases(text);
}

export function AddTestCasesToProjectModal({
  isOpen,
  onClose,
  testCasesToAdd,
  lastAssistantMessage,
  onImportFromMessage,
  onSaved,
}) {
  const [projects, setProjects] = useState([]);
  const [suites, setSuites] = useState([]);
  const [sections, setSections] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingSuites, setLoadingSuites] = useState(false);
  const [loadingSections, setLoadingSections] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showNewSection, setShowNewSection] = useState(false);
  const [newSectionName, setNewSectionName] = useState("");
  const [newSectionParentId, setNewSectionParentId] = useState("__root__");
  const [newSectionCreated,setNewSectionCreated]=useState(false)

  const [projectId, setProjectId] = useState("");
  const [suiteId, setSuiteId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [selectedTestCaseIds, setSelectedTestCaseIds] = useState(new Set());
  const [projectCaseTemplate, setProjectCaseTemplate] =
    useState("MULTIPLE_STEPS");

  const currentProject = projects.find((p) => p.project_id === projectId);
  const projectStructure =
    currentProject?.structure || currentProject?.project_type || "";
  const isMultiSuite =
    projectStructure === STRUCTURE_MULTI ||
    projectStructure === STRUCTURE_MULTIPLE ||
    String(projectStructure).toLowerCase().includes("multi");

  useEffect(() => {
    if (!isOpen || !projectId) {
      setProjectCaseTemplate("MULTIPLE_STEPS");
      return;
    }
    const fromList = currentProject?.test_case_structure_type;
    if (fromList) {
      setProjectCaseTemplate(normalizeProjectCaseTemplate(fromList));
      return;
    }
    let cancelled = false;
    projectService
      .getProject(projectId)
      .then((res) => {
        if (cancelled) return;
        const p = res?.data ?? res?.project ?? res;
        setProjectCaseTemplate(
          normalizeProjectCaseTemplate(p?.test_case_structure_type),
        );
      })
      .catch(() => {
        if (!cancelled) setProjectCaseTemplate("MULTIPLE_STEPS");
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, projectId, currentProject?.test_case_structure_type]);

  const caseTemplateHint = useMemo(() => {
    switch (projectCaseTemplate) {
      case "SINGLE_STEP":
        return "This project uses a single-step test case template. Imported steps will be merged into one step when you save.";
      case "BDD":
        return "This project uses BDD. AI imports may need Given/When/Then edits before they pass validation.";
      default:
        return "This project uses a multi-step test case template. Each imported step is stored as its own row.";
    }
  }, [projectCaseTemplate]);

  const loadProjects = useCallback(async () => {
    setLoadingProjects(true);
    try {
      const res = await projectService.getProjects({ limit: 100, page: 1 });
      const list =
        res?.data?.projects ??
        res?.projects ??
        (Array.isArray(res?.data) ? res.data : []) ??
        [];
      setProjects(list);
      if (list.length && !projectId) setProjectId(list[0].project_id);
    } catch (e) {
      toast.error("Failed to load projects");
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  const loadSuites = useCallback(async () => {
    if (!projectId || !isMultiSuite) {
      setSuites([]);
      return;
    }
    setLoadingSuites(true);
    try {
      const res = await suiteService.getSuites(projectId);
      const list =
        res?.suites ??
        res?.data?.suites ??
        (Array.isArray(res?.data) ? res.data : []) ??
        (Array.isArray(res) ? res : []) ??
        [];
      setSuites(list);
      setSuiteId(list[0]?.suite_id ?? "");
    } catch (e) {
      toast.error("Failed to load suites");
      setSuites([]);
    } finally {
      setLoadingSuites(false);
    }
  }, [projectId, isMultiSuite]);

  const loadSections = useCallback(async () => {
    if (!projectId) {
      setSections([]);
      return;
    }
    setLoadingSections(true);
    try {
      const res = await sectionService.getSectionTree(
        projectId,
        null,
        isMultiSuite ? suiteId || undefined : undefined,
      );
      const tree =
        res?.data?.sections ??
        res?.sections ??
        (Array.isArray(res?.data) ? res.data : []) ??
        [];
      setSections(flattenSections(tree));
      setSectionId("");
    } catch (e) {
      toast.error("Failed to load sections");
      setSections([]);
    } finally {
      setLoadingSections(false);
    }
  }, [projectId, suiteId, isMultiSuite]);

  useEffect(() => {
    if (!isOpen) return;
    loadProjects();
  }, [isOpen, loadProjects]);

  useEffect(() => {
    if (!isOpen) return;
    if (isMultiSuite) {
      loadSuites();
    } else {
      setSuites([]);
      setSuiteId("");
      loadSections();
    }
  }, [isOpen, projectId, isMultiSuite, loadSuites]);

  useEffect(() => {
    if (!isOpen) return;
    if (isMultiSuite && !suiteId) return;
    loadSections();
  }, [isOpen, projectId, suiteId, isMultiSuite, loadSections]);

  useEffect(() => {
    if (isOpen && testCasesToAdd?.length) {
      setSelectedTestCaseIds(new Set(testCasesToAdd.map((_, i) => i)));
    }
  }, [isOpen, testCasesToAdd]);

  const handleCreateSection = async () => {
    if (!newSectionName.trim() || !projectId) {
      toast.error("Section name and project are required");
      return;
    }
    try {
      const payload = {
        project_id: projectId,
        name: newSectionName.trim(),
        parent_section_id:
          newSectionParentId && newSectionParentId !== "__root__"
            ? newSectionParentId
            : null,
        suite_id: isMultiSuite && suiteId ? suiteId : null,
      };
      const res = await sectionService.createSection(payload);
      const created = res?.data ?? res;
      const sid = created?.section?.section_id ?? created?.id;
      if (sid) {
        setSections((prev) => [
          ...prev,
          { section_id: sid, name: newSectionName.trim(), level: 0 },
        ]);
        setSectionId(sid);
        setShowNewSection(false);
        setNewSectionName("");
        setNewSectionParentId("");
        setNewSectionCreated(true)
        toast.success("Section created");
      }
    } catch (e) {
      toast.error(e?.message || "Failed to create section");
    }
  };

  const handleSave = async () => {
    if (!projectId || !sectionId) {
      toast.error("Please select a project and a section (or create one).");
      return;
    }
    const toSave = (testCasesToAdd || []).filter((_, i) =>
      selectedTestCaseIds.has(i),
    );
    const targetSectionId = sectionId;
    if (!toSave.length) {
      toast.error("Select at least one test case to add.");
      return;
    }

    setSaving(true);
    let success = 0;
    let fail = 0;
    try {
      for (const tc of toSave) {
        try {
          const title = (tc.title || "").trim() || "Untitled test case";
          let steps;
          let extraPreconditions = "";

          if (Array.isArray(tc.stepsStructured) && tc.stepsStructured.length) {
            steps = tc.stepsStructured.map((row, i) => ({
              step_number: i + 1,
              action: String(row.action || "").trim(),
              expected_result: String(row.expected_result || "").trim(),
            }));
          } else {
            const stepsText =
              typeof tc.steps === "string"
                ? tc.steps
                : (tc.steps || []).join("\n");
            const stepLines = stepsText
              .split(/\n|\r\n/)
              .map((l) => l.trim())
              .filter(Boolean)
              .map((l) =>
                l.replace(/^[\d]+[.)]\s*/, "").replace(/^[-*]\s*/, ""),
              );
            const recovered = restructureLegacyPrefixedLines(title, stepLines);
            if (recovered) {
              steps = recovered.stepsStructured.map((row, i) => ({
                step_number: i + 1,
                action: String(row.action || "").trim(),
                expected_result: String(row.expected_result || "").trim(),
              }));
              if (recovered.preconditions)
                extraPreconditions = recovered.preconditions;
            } else {
              const folded = foldImpliedOutcomesInActionList(stepLines);
              steps =
                folded.length > 0
                  ? folded.map((row, i) => ({
                      step_number: i + 1,
                      action: String(row.action || "").trim(),
                      expected_result: String(row.expected_result || "").trim(),
                    }))
                  : [{ step_number: 1, action: title, expected_result: "" }];
            }
          }

          const payload = {
            title,
            section_id: targetSectionId,
            folder_id: targetSectionId,
            priority: "medium",
            status: "active",
            steps,
          };
          const preParts = [
            typeof tc.preconditions === "string" ? tc.preconditions.trim() : "",
            extraPreconditions.trim(),
          ].filter(Boolean);
          const pre = preParts.join("\n");
          if (pre) payload.preconditions = pre;
          if (isMultiSuite && suiteId) payload.suite_id = suiteId;

          const templateType = projectCaseTemplate;
          payload.test_case_structure_type = templateType;
          if (templateType === "SINGLE_STEP") {
            const collapsed = collapseStepsForSingleStepTemplate(payload.steps);
            payload.steps = collapsed.steps;
            const er = (collapsed.expected_result || "").trim();
            if (er) payload.expected_result = er;
          }

          await testcaseService.createTestCase(projectId, payload);
          success++;
        } catch (err) {
          console.error(err);
          fail++;
        }
      }
      if (success)
        toast.success(
          `Added ${success} test case(s)${fail ? ` (${fail} failed)` : ""}`,
        );
      else toast.error("Failed to add test cases");
      if (success && onSaved) onSaved();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedTestCaseIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedTestCaseIds.size === (testCasesToAdd?.length ?? 0)) {
      setSelectedTestCaseIds(new Set());
    } else {
      setSelectedTestCaseIds(new Set((testCasesToAdd || []).map((_, i) => i)));
    }
  };

  if (!isOpen) return null;

  const list = testCasesToAdd || [];
  const hasSelection = selectedTestCaseIds.size > 0;

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Add test cases to project</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto flex-1 space-y-4">
          {/* Project */}
          <div>
            <Label>Project</Label>
            <Select
              value={projectId}
              onValueChange={(v) => {
                setProjectId(v);
                setSectionId("");
                setSuiteId("");
              }}
              disabled={loadingProjects}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select project" />
              </SelectTrigger>
              <SelectContent>
                {projects.map((p) => (
                  <SelectItem key={p.project_id} value={p.project_id}>
                    {p.project_name ?? p.name ?? p.project_id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Suite (only for multi-suite) */}
          {isMultiSuite && (
            <div>
              <Label>Suite</Label>
              <Select
                value={suiteId}
                onValueChange={(v) => {
                  setSuiteId(v);
                  setSectionId("");
                }}
                disabled={loadingSuites}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select suite" />
                </SelectTrigger>
                <SelectContent>
                  {suites.map((s) => (
                    <SelectItem key={s.suite_id} value={s.suite_id}>
                      {s.suite_name ?? s.name ?? s.suite_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Section */}
          <div>
            <Label>Section</Label>
            {!showNewSection ? (
              <div className="space-y-2">
                <Select
                  value={sectionId}
                  onValueChange={setSectionId}
                  disabled={loadingSections || (isMultiSuite && !suiteId)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select section" />
                  </SelectTrigger>
                  <SelectContent>
                    {sections.map((s) => (
                      <SelectItem key={s.section_id} value={s.section_id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={newSectionCreated}
                  onClick={() => setShowNewSection(true)}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Create new section
                </Button>
              </div>
            ) : (
              <div className="border rounded-lg p-3 space-y-2">
                <Input
                  placeholder="New section name"
                  value={newSectionName}
                  onChange={(e) => setNewSectionName(e.target.value)}
                />
                <div>
                  <Label className="text-xs">Parent section (optional)</Label>
                  <Select
                    value={newSectionParentId}
                    onValueChange={setNewSectionParentId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="None (root)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__root__">None (root)</SelectItem>
                      {sections.map((s) => (
                        <SelectItem key={s.section_id} value={s.section_id}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2">
                  <Button type="button" size="sm" onClick={handleCreateSection}>
                    Create section
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowNewSection(false);
                      setNewSectionName("");
                      setNewSectionParentId("__root__");
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Which test cases */}
          <div>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <Label>Test cases to add</Label>
              <div className="flex gap-2">
                {lastAssistantMessage &&
                  typeof onImportFromMessage === "function" && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const parsed =
                          parseTestCasesFromMessage(lastAssistantMessage);
                        if (parsed.length) {
                          onImportFromMessage(parsed);
                          setSelectedTestCaseIds(
                            new Set(parsed.map((_, i) => i)),
                          );
                          toast.success(
                            `Imported ${parsed.length} test case(s) from last message`,
                          );
                        } else {
                          toast.error(
                            "No test cases found. Use “### Test Case: …” blocks, or a numbered list under a “Test cases” heading.",
                          );
                        }
                      }}
                    >
                      Import from last message
                    </Button>
                  )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={toggleSelectAll}
                >
                  {selectedTestCaseIds.size === list.length
                    ? "Deselect all"
                    : "Select all"}
                </Button>
              </div>
            </div>
            <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
              {list.length === 0 ? (
                <div className="p-4 text-sm text-gray-500">
                  No test cases added yet. Use “Import from last message” or add
                  manually from the chat.
                </div>
              ) : (
                list.map((tc, idx) => (
                  <label
                    key={idx}
                    className="flex items-start gap-2 p-3 cursor-pointer hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={selectedTestCaseIds.has(idx)}
                      onChange={() => toggleSelect(idx)}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">
                        {tc.title || "Untitled"}
                      </div>
                      <div className="text-xs text-gray-500 line-clamp-2">
                        {typeof tc.steps === "string"
                          ? tc.steps
                          : (tc.steps || []).join(" · ")}
                      </div>
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>
        </div>
        <div className="p-4 border-t flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !sectionId || !hasSelection}
          >
            {saving ? (
              <InlineLoadSkeleton className="h-4 w-4 mr-2" />
            ) : null}
            Save to project
          </Button>
        </div>
      </div>
    </div>
  );
}
